/**
 * Утилита для расшифровки логов
 * Позволяет преобразовывать сырые данные логов в человекочитаемый вид
 */

/**
 * Расшифровывает значение поля на основе dataMap и data
 * @param {string} fieldName - название поля
 * @param {*} value - значение поля
 * @param {Object} dataMap - карта расшифровки полей
 * @param {Object} data - данные для расшифровки
 * @returns {Object} - объект с расшифрованным значением и дополнительной информацией
 */
export const decodeFieldValue = (fieldName, value, dataMap = {}, data = {}) => {
  // Если нет dataMap для этого поля, возвращаем исходное значение
  if (!dataMap[fieldName]) {
    return {
      value: value,
      decoded: true,
      type: 'raw'
    };
  }

  const fieldMap = dataMap[fieldName];
  
  // Обработка массива значений
  if (Array.isArray(value)) {
    const decodedArray = value.map(item => decodeSingleValue(item, fieldMap, data));
    return {
      value: decodedArray,
      decoded: true,
      type: 'array',
      originalValue: value
    };
  }

  // Обработка одиночного значения
  const decodedValue = decodeSingleValue(value, fieldMap, data);
  return {
    value: decodedValue,
    decoded: true,
    type: 'single',
    originalValue: value
  };
};

/**
 * Расшифровывает одиночное значение
 * @param {*} value - значение для расшифровки
 * @param {Object} fieldMap - карта расшифровки для поля
 * @param {Object} data - данные для расшифровки
 * @returns {*} - расшифрованное значение
 */
const decodeSingleValue = (value, fieldMap, data) => {
  // Простая расшифровка по ключу (для обратной совместимости)
  if (typeof fieldMap === 'string') {
    const dataArray = data[fieldMap];
    if (Array.isArray(dataArray)) {
      const found = dataArray.find(item => item.id === value || item.fid === value);
      return found ? found.name || found.display_name || found.title : value;
    }
    return value;
  }

  // Новая сложная расшифровка с указанием key и value
  if (typeof fieldMap === 'object') {
    for (const [dataKey, config] of Object.entries(fieldMap)) {
      const dataArray = data[dataKey];
      if (Array.isArray(dataArray)) {
        // Проверяем, является ли config объектом с key и value
        if (config && typeof config === 'object' && config.key && config.value) {
          const found = dataArray.find(item => {
            // Ищем по указанному ключу
            return item[config.key] === value ||
                   (typeof value === 'string' && item[config.key]?.toString() === value);
          });
          if (found) {
            // Возвращаем значение из указанного поля
            return found[config.value] || value;
          }
        } else {
          // Старый формат для обратной совместимости
          const found = dataArray.find(item => {
            return item.id === value || 
                   item.fid === value || 
                   item[config] === value ||
                   (typeof value === 'string' && item[config]?.toString() === value);
          });
          if (found) {
            return found.name || found.display_name || found.title || found[config] || value;
          }
        }
      }
    }
  }

  return value;
};

/**
 * Обрабатывает все поля в объекте лога с использованием только formatting
 * @param {Object} logItem - элемент лога
 * @param {Object} dataMap - карта расшифровки (не используется, оставлен для совместимости)
 * @param {Object} data - данные для расшифровки (не используется, оставлен для совместимости)
 * @param {Object} translates - переводы названий полей
 * @param {Object} formatting - функции форматирования
 * @returns {Object} - объект с обработанными данными
 */
export const decodeLogItem = (logItem, dataMap = {}, data = {}, translates = {}, formatting = {}, ignore = []) => {
  const decoded = {
    ...logItem,
    decoded_fields: {},
    field_translations: {}
  };

  // Обработка old_values
  if (logItem.old_values) {
    decoded.old_values = {};
    for (const [field, value] of Object.entries(logItem.old_values)) {
      // Пропускаем игнорируемые поля
      if (ignore.includes(field)) {
        continue;
      }
      // Используем только форматирование, без расшифровки через data
      decoded.old_values[field] = value;
      decoded.decoded_fields[field] = {
        value: value,
        decoded: true,
        type: 'raw',
        originalValue: value
      };
      decoded.field_translations[field] = translates[field] || field;
    }
  }

  // Обработка new_values
  if (logItem.new_values) {
    decoded.new_values = {};
    for (const [field, value] of Object.entries(logItem.new_values)) {
      // Пропускаем игнорируемые поля
      if (ignore.includes(field)) {
        continue;
      }
      // Используем только форматирование, без расшифровки через data
      decoded.new_values[field] = value;
      if (!decoded.decoded_fields[field]) {
        decoded.decoded_fields[field] = {
          value: value,
          decoded: true,
          type: 'raw',
          originalValue: value
        };
        decoded.field_translations[field] = translates[field] || field;
      }
    }
  }

  // Обновляем список измененных полей, исключая игнорируемые
  if (logItem.changed_fields) {
    decoded.changed_fields = logItem.changed_fields.filter(field => !ignore.includes(field));
  }

  return decoded;
};

/**
 * Форматирует значение для отображения
 * @param {*} value - значение для форматирования
 * @param {string} fieldName - название поля
 * @param {Object} formatting - объект с функциями форматирования
 * @returns {string} - отформатированное значение
 */
export const formatValueForDisplay = (value, fieldName = '', formatting = {}, rawItem) => {
  if (value === null || value === undefined || value === '') {
    return 'Пустое значение';
  }
  console.log(value)

  // Проверяем, есть ли пользовательская функция форматирования для этого поля
  if (formatting[fieldName] && typeof formatting[fieldName] === 'function') {
    try {
      return formatting[fieldName](value, rawItem);
    } catch (error) {
      console.warn(`Ошибка форматирования поля ${fieldName}:`, error);
      // Возвращаем исходное значение в случае ошибки
    }
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return 'Пусто';
    }
    return value.join(', ');
  }

  if (typeof value === 'boolean') {
    return value ? 'Да' : 'Нет';
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  return String(value);
};

/**
 * Получает цвет для типа действия
 * @param {string} action - тип действия
 * @param {boolean} darkMode - темный режим
 * @returns {Object} - объект с цветами
 */
export const getActionColors = (action, darkMode = false) => {
  if (darkMode) {
    switch (action) {
      case 'INSERT':
        return { bg: 'rgba(76, 175, 80, 0.2)', color: '#81c784', border: '#4caf50' };
      case 'UPDATE':
        return { bg: 'rgba(33, 150, 243, 0.2)', color: '#64b5f6', border: '#2196f3' };
      case 'DELETE':
        return { bg: 'rgba(244, 67, 54, 0.2)', color: '#e57373', border: '#f44336' };
      default:
        return { bg: 'rgba(158, 158, 158, 0.2)', color: '#bdbdbd', border: '#9e9e9e' };
    }
  } else {
    switch (action) {
      case 'INSERT':
        return { bg: 'rgba(76, 175, 80, 0.1)', color: '#4caf50', border: '#4caf50' };
      case 'UPDATE':
        return { bg: 'rgba(33, 150, 243, 0.1)', color: '#2196f3', border: '#2196f3' };
      case 'DELETE':
        return { bg: 'rgba(244, 67, 54, 0.1)', color: '#f44336', border: '#f44336' };
      default:
        return { bg: 'rgba(158, 158, 158, 0.1)', color: '#9e9e9e', border: '#9e9e9e' };
    }
  }
};

/**
 * Получает текст для типа действия
 * @param {string} action - тип действия
 * @returns {string} - человекочитаемый текст
 */
export const getActionText = (action) => {
  switch (action) {
    case 'INSERT':
      return 'Создание';
    case 'UPDATE':
      return 'Изменение';
    case 'DELETE':
      return 'Удаление';
    default:
      return action;
  }
}; 